import { useMutation, type DocumentNode } from '@apollo/client'
import { SettingsBackupRestore } from '@mui/icons-material'
import { Badge, Box, Button, Stack } from '@mui/material'
import { RosterProfileQuery } from '__generated__/graphql'
import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { candidateTableColumns } from 'recruiter/components/candidateTableColumns'
import { EditRoster } from 'recruiter/components/RosterDrawer/AddOrEdit.gql'
import { useCandidateSqlSearch } from 'recruiter/hooks'
import { CandidateSearchSql } from 'recruiter/pages/Research/CandidateSearchSql'
import SaveMapping from 'recruiter/pages/Research/SaveMapping'
import SendToSearch from 'recruiter/pages/Research/SendToSearch'
import useToast from 'shared/components/Toast/useToast'
import {
    type CandidateFilterState,
    areFiltersEqual,
    defaultFilters,
} from 'shared/hooks/candidateSearchUrlState'
import { wrapCandidateFilterState } from 'shared/utils/mappingFilterConverter'

interface RosterProfileMappingCandidateViewProps {
    roster: NonNullable<RosterProfileQuery['roster']>
    refetch: () => void
}

// Helper to check if URL has any filter parameters
const hasUrlFilterParams = (searchParams: URLSearchParams): boolean => {
    // Check for common filter params (excluding pagination params)
    const filterParamKeys = [
        'colorNames',
        'name',
        'recruitingStatus',
        'outreachStatus',
        'starRatingMin',
        'starRatingMax',
        'screeningNoteTemplateIds',
        'screeningNoteStarRatingMin',
        'screeningNoteStarRatingMax',
        'companyIds',
        'companyTimeFrame',
        'companyRangeGte',
        'companyRangeLte',
        'sectorNames',
        'sectorTimeFrame',
        'sectorRangeGte',
        'sectorRangeLte',
        'industryNames',
        'industryTimeFrame',
        'industryRangeGte',
        'industryRangeLte',
        'positionTitles',
        'positionTimeFrame',
        'positionRangeGte',
        'positionRangeLte',
        'locationIds',
        'locationTimeFrame',
        'locationRangeGte',
        'locationRangeLte',
        'locationIncludeStateCountry',
        'analystClassYearGte',
        'analystClassYearLte',
        'referenceRosterIds',
        'referenceRosterProcessStatusIds',
        'genderIds',
        'ethnicityIds',
        'hometownSearchTerms',
        'citizenshipIds',
        'visaStatusIds',
        'visaStatusIdsNot',
        'qualitativeIds',
        'qualitativeIdsNot',
        'revenueExperienceIds',
        'lgbtqIds',
        'cpiPlacedIds',
        'cpiPlacedIdsNot',
        'referenceAttributeIds',
        'mbaTierIds',
        'industryPrefFirst',
        'industryPrefSecond',
        'industryPrefThird',
        'sectorPrefFirst',
        'sectorPrefSecond',
        'sectorPrefThird',
        'locationPrefFirst',
        'locationPrefSecond',
        'locationPrefThird',
        'undergradCollegeIds',
        'mbaCollegeIds',
        'majorIds',
        'degreeTypeIds',
        'degreeTypeIdsNot',
        'gpaRangeMin',
        'gpaRangeMax',
        'graduationRangeGte',
        'graduationRangeLte',
        'graduationEducationType',
    ]

    return filterParamKeys.some((key) => searchParams.has(key))
}

export function RosterProfileMappingCandidateView({
    roster,
    refetch,
}: RosterProfileMappingCandidateViewProps) {
    const toast = useToast()
    const [searchParams] = useSearchParams()
    const { tableState, setTableState, selectedCandidateIds } =
        useCandidateSqlSearch()
    const filters = tableState.filters
    const setFilters = (newFilters: CandidateFilterState) =>
        setTableState({ filters: newFilters })
    const [savedFilters, setSavedFilters] =
        useState<CandidateFilterState | null>(null)
    const [isDirty, setIsDirty] = React.useState(false)
    const canEdit = roster.permissions?.canEdit

    const getDefaultFilters = (): CandidateFilterState => ({
        ...defaultFilters,
    })

    // Load saved filters from backend (always new format)
    // Only apply saved filters if URL doesn't have filter params (URL params take priority)
    useEffect(() => {
        const savedFilter = roster?.mapping?.mappingFilters
        const defaultFilters = getDefaultFilters()

        // Always load saved filters for comparison (to show dirty state)
        if (savedFilter) {
            const filterState = savedFilter as unknown as CandidateFilterState
            setSavedFilters(filterState)
        } else {
            setSavedFilters(defaultFilters)
        }

        // Only apply saved filters if URL doesn't have filter params
        // URL params take priority - let useRosterSearchWithFilters handle URL-based filters
        if (!hasUrlFilterParams(searchParams)) {
            if (savedFilter) {
                // Backend always returns new format (CandidateFilterState)
                // Backend handles legacy → new conversion transparently
                // Resolver returns GraphQLJSON (unknown), but actually returns CandidateFilterState
                const filterState =
                    savedFilter as unknown as CandidateFilterState
                setFilters(filterState)
            } else {
                setFilters(defaultFilters)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roster?.mapping?.mappingFilters])

    const [updateRoster] = useMutation(EditRoster as DocumentNode)

    const handleSaveMapping = () => {
        try {
            const currentFilters = filters // from useCandidateSqlSearch

            // Wrap with nested type/version structure before saving
            const wrapped = wrapCandidateFilterState(currentFilters)

            updateRoster({
                variables: {
                    rosterId: roster.id,
                    mappingFilter: wrapped, // Save with nested structure: { type, version, filters }
                },
                onCompleted: (res) => {
                    if (res.updateRoster.success) {
                        toast({
                            title: 'Mapping Saved',
                            message: 'Mapping saved successfully',
                            severity: 'success',
                        })
                        refetch()
                    } else {
                        toast({
                            title: 'Save Error',
                            message:
                                res.updateRoster.message ||
                                'Error saving mapping',
                            severity: 'error',
                        })
                    }
                },
                onError: (error) => {
                    console.error('Error saving mapping:', error)
                    toast({
                        title: 'Save Error',
                        message: error.message || 'Error saving mapping',
                        severity: 'error',
                    })
                },
            })
        } catch (error) {
            console.error('Error saving mapping:', error)
        }
    }

    // Check if filters are dirty
    useEffect(() => {
        if (!savedFilters) {
            setIsDirty(false)
            return
        }

        // Use proper deep comparison that handles undefined values correctly
        // This ensures that removing a name field (setting to undefined) is detected as a change
        setIsDirty(!areFiltersEqual(filters, savedFilters))
    }, [filters, savedFilters])

    const resetButton = () => {
        return (
            <Button
                disabled={!canEdit || !isDirty || !savedFilters}
                variant="outlined"
                size="small"
                onClick={() => {
                    if (savedFilters) {
                        setFilters(savedFilters)
                    }
                }}
                sx={{ minWidth: 'auto' }}
            >
                <Stack direction="row" spacing={1} alignItems="center">
                    <SettingsBackupRestore fontSize="small" />
                </Stack>
            </Button>
        )
    }

    const actionButtons = (
        <>
            <SendToSearch selectedCandidates={selectedCandidateIds} />
            {canEdit && (
                <>
                    <Box sx={{ display: 'none' }}>
                        <SaveMapping
                            onClick={handleSaveMapping}
                            isDisabled={!canEdit || !isDirty}
                        />
                    </Box>
                    {!savedFilters || !isDirty ? (
                        <>{resetButton()}</>
                    ) : (
                        <Badge
                            color="primary"
                            variant="standard"
                            badgeContent=""
                        >
                            {resetButton()}
                        </Badge>
                    )}
                </>
            )}
        </>
    )

    return (
        <CandidateSearchSql
            columns={candidateTableColumns}
            topActionButtons={actionButtons}
        />
    )
}
